/**
 * FileMessage - Displays file attachments in chat messages
 * 
 * Features:
 * - Image previews with lightbox
 * - File download links
 * - File type icons
 * - Size formatting
 * - Professional design
 */

import React, { useState } from 'react';
import { File, Image, Download, ExternalLink, Eye, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface FileMessageProps {
  attachment: {
    id: string;
    originalFilename: string;
    fileSize: number;
    mimeType: string;
    fileCategory: 'image' | 'document' | 'audio' | 'video';
    isImage: boolean;
    r2Url?: string;
    createdAt: number;
  };
  isOwn?: boolean;
  showSender?: boolean;
}

interface ImageViewerProps {
  imageUrl: string;
  filename: string;
  onClose: () => void;
}

// Format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Get file type icon
const getFileTypeIcon = (mimeType: string, isImage: boolean) => {
  if (isImage) {
    return <Image className="w-5 h-5" />;
  }
  
  if (mimeType.includes('pdf')) {
    return <File className="w-5 h-5 text-red-500" />;
  }
  
  if (mimeType.includes('word') || mimeType.includes('document')) {
    return <File className="w-5 h-5 text-blue-500" />;
  }
  
  return <File className="w-5 h-5 text-gray-500" />;
};

// Get file type badge color
const getFileTypeBadge = (mimeType: string): { label: string; color: string } => {
  if (mimeType.includes('image')) {
    return { label: 'Image', color: 'bg-green-100 text-green-800' };
  }
  
  if (mimeType.includes('pdf')) {
    return { label: 'PDF', color: 'bg-red-100 text-red-800' };
  }
  
  if (mimeType.includes('word') || mimeType.includes('document')) {
    return { label: 'DOC', color: 'bg-blue-100 text-blue-800' };
  }
  
  if (mimeType.includes('text')) {
    return { label: 'TXT', color: 'bg-gray-100 text-gray-800' };
  }
  
  return { label: 'FILE', color: 'bg-gray-100 text-gray-800' };
};

// Image Viewer Modal
const ImageViewer: React.FC<ImageViewerProps> = ({ imageUrl, filename, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="relative max-w-4xl max-h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-white rounded-t-lg">
          <div className="flex items-center gap-3">
            <Image className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-900 truncate">{filename}</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Image */}
        <div className="flex-1 bg-gray-900 flex items-center justify-center p-4">
          <img 
            src={imageUrl} 
            alt={filename}
            className="max-w-full max-h-[70vh] object-contain rounded"
          />
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between p-4 bg-white rounded-b-lg">
          <div className="text-sm text-gray-600">
            Click outside to close
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => window.open(imageUrl, '_blank')}
            className="flex items-center gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Open Original
          </Button>
        </div>
      </div>
      
      {/* Click outside to close */}
      <div 
        className="absolute inset-0 -z-10" 
        onClick={onClose}
      />
    </div>
  );
};

export const FileMessage: React.FC<FileMessageProps> = ({ 
  attachment, 
  isOwn = false,
  showSender = false 
}) => {
  const [showImageViewer, setShowImageViewer] = useState(false);
  
  const { label: typeLabel, color: typeColor } = getFileTypeBadge(attachment.mimeType);
  const fileIcon = getFileTypeIcon(attachment.mimeType, attachment.isImage);

  const handleDownload = () => {
    if (attachment.r2Url) {
      const link = document.createElement('a');
      link.href = attachment.r2Url;
      link.download = attachment.originalFilename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleImageClick = () => {
    if (attachment.isImage && attachment.r2Url) {
      setShowImageViewer(true);
    }
  };

  return (
    <>
      <div className={`
        max-w-sm rounded-lg border shadow-sm
        ${isOwn 
          ? 'bg-blue-50 border-blue-200' 
          : 'bg-white border-gray-200'
        }
      `}>
        
        {/* Image Preview */}
        {attachment.isImage && attachment.r2Url && (
          <div className="relative">
            <img
              src={attachment.r2Url}
              alt={attachment.originalFilename}
              className="w-full h-48 object-cover rounded-t-lg cursor-pointer hover:opacity-90 transition-opacity"
              onClick={handleImageClick}
              loading="lazy"
            />
            
            {/* Image Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-t-lg" />
            
            {/* View Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleImageClick}
              className="absolute top-2 right-2 bg-black/50 text-white hover:bg-black/70 h-8 w-8 p-0"
            >
              <Eye className="w-4 h-4" />
            </Button>
          </div>
        )}
        
        {/* File Info */}
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* File Icon (for non-images) */}
            {!attachment.isImage && (
              <div className="flex-shrink-0 mt-0.5">
                {fileIcon}
              </div>
            )}
            
            {/* File Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline" className={`text-xs ${typeColor}`}>
                  {typeLabel}
                </Badge>
                <span className="text-xs text-gray-500">
                  {formatFileSize(attachment.fileSize)}
                </span>
              </div>
              
              <p className="text-sm font-medium text-gray-900 truncate mb-2">
                {attachment.originalFilename}
              </p>
              
              {/* Actions */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownload}
                  className="flex items-center gap-1 h-7 text-xs"
                >
                  <Download className="w-3 h-3" />
                  Download
                </Button>
                
                {attachment.r2Url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(attachment.r2Url, '_blank')}
                    className="flex items-center gap-1 h-7 text-xs"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Open
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Image Viewer Modal */}
      {showImageViewer && attachment.r2Url && (
        <ImageViewer
          imageUrl={attachment.r2Url}
          filename={attachment.originalFilename}
          onClose={() => setShowImageViewer(false)}
        />
      )}
    </>
  );
};

export default FileMessage;